import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { JiraIcon } from "@/components/icons";
import { JiraManualDialog } from "@/components/settings/jira-manual-dialog";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Key,
  Layers,
  RefreshCw,
  Webhook,
} from "lucide-react";

export function JiraIntegration() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isManualOpen, setIsManualOpen] = useState(false);

  const {
    data: connections,
    isLoading,
    refetch,
  } = trpc.integration.listConnections.useQuery({ provider: "jira" });

  const refreshWebhookMutation =
    trpc.integration.jira.refreshWebhook.useMutation({
      onSuccess: (res) => {
        if (res.refreshed) {
          toast.success(
            `Webhook refreshed (expires ${new Date(res.expiresAt).toLocaleDateString()})`,
          );
        } else {
          toast.info("No active dynamic webhooks to refresh; re-registered");
        }
        refetch();
      },
      onError: (err) => {
        toastTrpcError(err, "Failed to refresh webhook");
      },
    });

  useEffect(() => {
    const connected = searchParams.get("connected");
    const site = searchParams.get("site");
    const error = searchParams.get("error");

    if (connected === "jira") {
      toast.success(
        site ? `Connected to Jira: ${site}` : "Successfully connected to Jira",
      );
      searchParams.delete("connected");
      searchParams.delete("site");
      setSearchParams(searchParams, { replace: true });
      refetch();
    } else if (error) {
      toast.error(`Jira connection error: ${error}`);
      searchParams.delete("error");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch]);

  const handleConnect = () => {
    window.location.href = "/api/integrations/jira/connect";
  };

  const jiraConnections =
    connections?.filter((c) => c.provider === "jira") ?? [];
  const hasConnections = jiraConnections.length > 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <JiraIcon className="h-4 w-4 text-blue-500 fill-current" />
                Jira Cloud
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Connect Jira Cloud via OAuth 2.0 (3LO) or Admin API token to
                link canonical findings, create issues, and sync triage status.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                onClick={() => setIsManualOpen(true)}
                variant="outline"
                size="sm"
                className="gap-1.5"
              >
                <Key className="h-3.5 w-3.5" />
                Connect Manually
              </Button>
              <Button onClick={handleConnect} variant="default" size="sm">
                {hasConnections ? "Connect via OAuth" : "Connect Jira"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
            </div>
          ) : !hasConnections ? (
            <div className="flex items-center gap-2.5 rounded-lg bg-muted/30 p-4 text-sm text-muted-foreground">
              <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>
                No Jira sites connected yet. Use the buttons above to connect
                via OAuth or an Atlassian API token.
              </span>
            </div>
          ) : (
            <div className="divide-y divide-border/60 rounded-lg bg-muted/20">
              {jiraConnections.map((conn) => {
                const isNeedsReauth = conn.status === "needs_reauth";
                const isActive = conn.status === "active";
                const hasExpiry = Boolean(conn.webhookExpiresAt);

                return (
                  <div
                    key={conn.id}
                    className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">
                          {conn.displayName || "Jira Site"}
                        </p>
                        {isActive && (
                          <Badge
                            variant="outline"
                            className="rounded-sm gap-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-600 text-xs"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Active
                          </Badge>
                        )}
                        {isNeedsReauth && (
                          <Badge
                            variant="destructive"
                            className="rounded-sm gap-1 text-xs"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Re-auth Required
                          </Badge>
                        )}
                        {!isActive && !isNeedsReauth && (
                          <Badge
                            variant="secondary"
                            className="rounded-sm capitalize text-xs"
                          >
                            {conn.status}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {conn.siteUrl && (
                          <a
                            href={conn.siteUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 hover:text-foreground hover:underline"
                          >
                            {conn.siteUrl}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {hasExpiry && conn.webhookExpiresAt && (
                          <span className="flex items-center gap-1">
                            <Webhook className="h-3 w-3 text-muted-foreground" />
                            Webhook expires{" "}
                            {new Date(
                              conn.webhookExpiresAt,
                            ).toLocaleDateString()}
                          </span>
                        )}
                        {conn.webhookRouteId && (
                          <span className="font-mono text-xs">
                            route: {conn.webhookRouteId.slice(0, 8)}...
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {hasExpiry && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            refreshWebhookMutation.mutate({
                              connectionId: conn.id,
                            })
                          }
                          disabled={refreshWebhookMutation.isPending}
                          className="gap-1 text-xs"
                        >
                          <RefreshCw
                            className={`h-3 w-3 ${
                              refreshWebhookMutation.isPending
                                ? "animate-spin"
                                : ""
                            }`}
                          />
                          Refresh webhook
                        </Button>
                      )}
                      {isNeedsReauth ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handleConnect}
                          className="gap-1.5"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          Reconnect Jira
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleConnect}
                          className="gap-1.5 text-xs text-muted-foreground"
                        >
                          Manage
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {jiraConnections.some((c) => c.status === "needs_reauth") && (
            <div className="flex gap-3 rounded-lg bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                One or more Jira connections require re-authorization. Refresh
                tokens may have expired or been revoked in Atlassian. Click
                Reconnect Jira to resume synchronization.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <JiraManualDialog
        open={isManualOpen}
        onOpenChange={setIsManualOpen}
        onConnected={refetch}
      />
    </>
  );
}
